import { BASE_URL } from "../config/api";

// export const BASE_URL = isDev
//   ? "http://localhost:5121/api"
//   : "https://twdcapitesting.riteliberia.org/api";

const TOKEN_KEY = "twdc_access_token";
const REFRESH_TOKEN_KEY = "twdc_refresh_token";

export const getAccessToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const setTokens = (tokens: {
  accessToken?: string;
  refreshToken?: string;
}) => {
  if (tokens.accessToken) {
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
  }
  if (tokens.refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  }
};

export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem("twdc_username");
  localStorage.removeItem("twdc_first_name");
  localStorage.removeItem("twdc_role_name");
  localStorage.removeItem("twdc_role_permissions");
  localStorage.removeItem("twdc_user_image");
};

// Track ongoing refresh to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = async (): Promise<string | null> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        // Refresh token is invalid or expired
        clearAuth();
        // Redirect to login page
        window.location.href = "/login";
        return null;
      }

      const data: { accessToken?: string; refreshToken?: string } =
        await response.json();

      if (data.accessToken) {
        setTokens({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken || refreshToken,
        });
        return data.accessToken;
      }

      return null;
    } catch (error) {
      console.error("Token refresh failed:", error);
      clearAuth();
      window.location.href = "/login";
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const apiRequest = async (
  path: string,
  options: RequestInit = {},
): Promise<Response> => {
  const headers = new Headers(options.headers ?? {});
  const token = getAccessToken();

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  let response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  // If not unauthorized, return the response
  if (response.status !== 401) {
    return response;
  }

  // Check if we have a refresh token before attempting refresh
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    // No refresh token available, redirect to login
    clearAuth();
    window.location.href = "/login";
    return response;
  }

  // Try to refresh the access token
  const newToken = await refreshAccessToken();

  // If refresh failed, return the original 401 response
  if (!newToken) {
    return response;
  }

  // Retry the original request with the new token
  headers.set("Authorization", `Bearer ${newToken}`);
  response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  return response;
};
